import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, Form, FormField, FormHelp, Input, Label, LabeledControl, Select } from '@zillow/constellation-10';

import {
  type GetUserPropertyDetailsListingCriteriaDetailsWebDto,
  type MultiplexBuildingSessionListingCriterionWebDto,
  RentalManagerListingCriteriaType,
} from '@zg-rentals/ts-rental-manager-api-client';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useScreenSize } from '../../../hooks';
import { useIsOnReviewStep, useMultiplexListingFlow } from '../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useCriteriaSaveMutation } from '../../../hooks/useCriteriaSaveMutation';
import { useFieldSave } from '../../../hooks/useFieldSave';
import accessUtils from '../../../utils/accessUtils';
import numberUtils from '../../../utils/numberUtils';

import { TRIGGER_LOCATIONS, TRIGGER_OBJECT } from '../../../constants/ClickstreamConstants';
import {
  LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS,
  NOT_APPLICABLE,
  STRING_VALUE_TO_NUMBER_VALUE_MAP,
} from '../../../constants/ScreeningCriteriaConstants';
import Criterion from '../../../model/ListingCriteria/Criterion';

import {
  emitIncomeRatioEvent,
  emitIncomeRentNegotiableEvent,
  EVENT_IDS,
} from '../../../analytics/events/screeningCriteria';
import { useAuth } from '../../AuthProvider';

import type { ChangeEvent } from 'react';
import type { SetListingCriteriaMutationVariables } from '../../../hooks/useSetListingCriteriaMutation';

const inputMinRatioName = 'criterion';
const inputNegotiableName = 'negotiable';

type Props = {
  /** Building-session criteria during multiplex wizard steps; omit on review / non-multiplex. */
  criteria?: Array<MultiplexBuildingSessionListingCriterionWebDto>;
  incomeToRentRatioMax?: string;
  price?: number;
};

const IncomeToRentRatioFormField = ({
  criteria,
  incomeToRentRatioMax: incomeToRentRatioMaxProp,
  price: priceProp,
}: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const formModeContext = useFormMode();
  const { isScreenDesktop } = useScreenSize();
  const isOnReviewStep = useIsOnReviewStep();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const triggerLocation = listingDetails?.active ? TRIGGER_LOCATIONS.POST_ACTIVATION : TRIGGER_LOCATIONS.POSTING_PATH;
  const triggerObject =
    formModeContext.mode == 'modal' ? TRIGGER_OBJECT.REVIEW : TRIGGER_OBJECT.FINANCIAL_EXPECTATIONS_PAGE;
  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible,
  });

  const listingCriteria = useMemo<Array<GetUserPropertyDetailsListingCriteriaDetailsWebDto>>(
    () => listingDetails?.listingCriteria?.criteria || [],
    [listingDetails?.listingCriteria?.criteria],
  );

  const effectiveCriteria = criteria ?? listingCriteria;

  const incomeToRentRatioMax =
    incomeToRentRatioMaxProp ??
    effectiveCriteria.find((obj) => obj.type === RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO)?.max ??
    '';

  const price = priceProp ?? listingDetails?.listingCriteria?.price ?? 0;
  const isMultifamily = listingDetails?.isMultifamily || false;

  const { initCriterionValue, initialNegotiableValue, targetCriteria } = useMemo(() => {
    const foundIndex = effectiveCriteria.findIndex(
      (obj) => obj.type === RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO,
    );
    const targetCriteria = effectiveCriteria[foundIndex];

    return {
      initCriterionValue: targetCriteria?.criterion || NOT_APPLICABLE,
      initialNegotiableValue: targetCriteria?.negotiable || false,
      targetCriteria,
    };
  }, [effectiveCriteria]);

  const filteredLCMinIncomeToRentRatioOptions = useMemo(() => {
    return incomeToRentRatioMax === ''
      ? LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS
      : LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS.filter((option) => {
          if (option.value === NOT_APPLICABLE) {
            return true;
          }
          const optionValue = option.value.toString().replace('x', '');
          const incomeToRentRatioMaxValue = incomeToRentRatioMax.toString().replace('x', '');
          return Number(optionValue) <= Number(incomeToRentRatioMaxValue);
        });
  }, [incomeToRentRatioMax]);

  const { mutation: updateMutation, isBatched } = useCriteriaSaveMutation({
    successToastMessage: 'Income-to-rent ratio updated',
  });

  const ratioField = useFieldSave({
    fieldName: inputMinRatioName,
    initialValue: initCriterionValue,
    mutation: updateMutation,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          ...targetCriteria,
          criterion: value === '' ? NOT_APPLICABLE : String(value),
          negotiable: negotiableField.value as boolean,
          isEnabled: true,
          type: RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO,
        }),
      ],
    }),
  });

  const negotiableField = useFieldSave({
    fieldName: inputNegotiableName,
    initialValue: initialNegotiableValue,
    mutation: updateMutation,
    autoSaveOnChange: true,
    autoSaveDebounceMs: isBatched ? 0 : undefined,
    buildMutationVariables: (value): SetListingCriteriaMutationVariables => ({
      getParams: { listingAlias },
      postData: [
        Criterion({
          ...targetCriteria,
          criterion: ratioField.value === '' ? NOT_APPLICABLE : String(ratioField.value),
          negotiable: Boolean(value),
          isEnabled: true,
          type: RentalManagerListingCriteriaType.INCOME_TO_RENT_RATIO,
        }),
      ],
    }),
  });

  const handleRatioChange = (event: ChangeEvent<HTMLSelectElement>) => {
    ratioField.onChange(event.target.value);
    if (listingDetails && user) {
      emitIncomeRatioEvent({
        currentProperty: listingDetails,
        user,
        triggerLocation,
        triggerObject,
      });
    }
  };

  const handleNegotiableChange = (event: ChangeEvent<HTMLInputElement>) => {
    negotiableField.onChange(event.target.checked);
    if (listingDetails && user) {
      emitIncomeRentNegotiableEvent({
        currentProperty: listingDetails,
        user,
        triggerLocation,
        triggerObject,
      });
    }
  };

  const minimumIncome = useMemo(() => {
    if (ratioField.value === '' || ratioField.value === NOT_APPLICABLE || !price) {
      return '--';
    }
    const multiplier = STRING_VALUE_TO_NUMBER_VALUE_MAP[ratioField.value as string] ?? 0;
    return numberUtils.asCurrency(price * multiplier);
  }, [ratioField.value, price]);

  const negotiableControl = (
    <LabeledControl
      label={<Label>Income-to-rent ratio is negotiable</Label>}
      control={
        <Checkbox
          name={inputNegotiableName}
          checked={Boolean(negotiableField.value)}
          onChange={handleNegotiableChange}
          data-testid="IncomeToRentRatioFormField-NegotiableCheckbox"
          data-eventid={EVENT_IDS.INCOME_RENT_NEGOTIABLE}
        />
      }
    />
  );

  return (
    <Form asChild={true} data-testid="IncomeToRentRatioFormField" onSubmit={(event) => event.preventDefault()}>
      <div>
        <FormField
          label={<Label>Minimum income-to-rent ratio</Label>}
          control={
            <Select
              size={isScreenDesktop ? 'lg' : 'md'}
              name={inputMinRatioName}
              value={String(ratioField.value ?? '')}
              onChange={handleRatioChange}
              onBlur={ratioField.onBlur}
              css={{ whiteSpace: 'pre' }}
              data-eventid={EVENT_IDS.INCOME_RENT_RATIO}
            >
              {filteredLCMinIncomeToRentRatioOptions.map(({ label, value }) => {
                return (
                  <option key={value as string} value={value}>
                    {label}
                  </option>
                );
              })}
            </Select>
          }
          description={
            isInMultiplexListingFlow && !isOnReviewStep ? undefined : (
              <FormHelp>
                {`${canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Rent'} is set at ${numberUtils.asCurrency(price)}/month`}
              </FormHelp>
            )
          }
        />

        {!isMultifamily && negotiableControl}

        {Boolean(price) && (
          <FormField
            label={<Label>Minimum monthly pre-tax income</Label>}
            control={<Input value={minimumIncome} readOnly={true} />}
          />
        )}

        {isMultifamily && negotiableControl}
      </div>
    </Form>
  );
};

export default IncomeToRentRatioFormField;
